import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiMenu, FiX, FiTrendingUp } from 'react-icons/fi';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (e, targetId) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <nav className={`fixed w-full z-50 transition-all duration-500 border-b ${isScrolled || location.pathname !== '/' ? 'bg-white/95 backdrop-blur-lg border-slate-200/50 shadow-sm py-4' : 'bg-transparent border-transparent py-6'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          
          {/* Logo */}
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
            <Link to="/" className="flex items-center gap-3 group">
              <div className={`p-2.5 rounded-xl transition-colors duration-300 ${isScrolled || location.pathname !== '/' ? 'bg-emerald-600 shadow-lg shadow-emerald-600/20' : 'bg-white/10 backdrop-blur-sm border border-white/20 group-hover:bg-white/20'}`}>
                <SafeIcon icon={FiTrendingUp} className={`text-2xl ${isScrolled || location.pathname !== '/' ? 'text-white' : 'text-emerald-400'}`} />
              </div>
              <span className={`text-2xl font-extrabold tracking-tight transition-colors duration-300 ${isScrolled || location.pathname !== '/' ? 'text-slate-900' : 'text-white'}`}>
                Toptier<span className={isScrolled || location.pathname !== '/' ? 'text-emerald-600' : 'text-emerald-400'}>Ventures</span>
              </span>
            </Link>
          </motion.div>

          {/* Desktop Nav */}
          <motion.div 
            initial={{ opacity: 0, y: -10 }} 
            animate={{ opacity: 1, y: 0 }} 
            className="hidden md:flex items-center space-x-1"
          >
            <a href="#how-it-works" onClick={(e) => handleNavClick(e, 'how-it-works')} className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 cursor-pointer ${isScrolled || location.pathname !== '/' ? 'text-slate-600 hover:text-emerald-600 hover:bg-emerald-50' : 'text-slate-200 hover:text-white hover:bg-white/10'}`}>Curriculum</a>
            <a href="#benefits" onClick={(e) => handleNavClick(e, 'benefits')} className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 cursor-pointer ${isScrolled || location.pathname !== '/' ? 'text-slate-600 hover:text-emerald-600 hover:bg-emerald-50' : 'text-slate-200 hover:text-white hover:bg-white/10'}`}>Why Us</a>
            <a href="#get-offer" onClick={(e) => handleNavClick(e, 'get-offer')} className={`ml-4 px-6 py-2.5 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5 cursor-pointer ${isScrolled || location.pathname !== '/' ? 'bg-emerald-600 text-white shadow-emerald-600/25 hover:bg-emerald-700' : 'bg-white text-emerald-700 shadow-black/10 hover:bg-slate-50'}`}>
              Start Learning
            </a>
          </motion.div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className={`p-2 rounded-lg transition-colors ${isScrolled || location.pathname !== '/' ? 'text-slate-900 hover:bg-slate-100' : 'text-white hover:bg-white/10'}`}>
              <SafeIcon icon={isMobileMenuOpen ? FiX : FiMenu} className="text-2xl" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }} 
            animate={{ opacity: 1, height: 'auto' }} 
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden absolute top-full left-0 w-full bg-white/95 backdrop-blur-xl border-b border-slate-200 shadow-xl overflow-hidden"
          >
            <div className="flex flex-col items-center py-6 space-y-4 px-4">
              <a href="#how-it-works" onClick={(e) => handleNavClick(e, 'how-it-works')} className="text-slate-600 font-medium w-full text-center py-3 rounded-xl hover:bg-slate-50 transition-colors">Curriculum</a>
              <a href="#benefits" onClick={(e) => handleNavClick(e, 'benefits')} className="text-slate-600 font-medium w-full text-center py-3 rounded-xl hover:bg-slate-50 transition-colors">Why Us</a>
              <a href="#get-offer" onClick={(e) => handleNavClick(e, 'get-offer')} className="bg-emerald-600 text-white px-8 py-4 rounded-xl font-semibold w-full text-center shadow-lg shadow-emerald-600/20">Start Learning</a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;